import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { AlertCircle, CheckCircle, Archive, Send, Edit3 } from 'lucide-react';

/**
 * Admin Wrong Info Review Page
 * Shows all contacts flagged with wrong information for admin review
 */
export default function WrongInfoReviewPage() {
  const ctx = useContext(AppContext);
  const { restaurants, users, currentUser, showToast, updateRestaurant } = ctx;
  const [reviewingId, setReviewingId] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [action, setAction] = useState(''); // 'correct', 'archive', 'return'

  // Filter restaurants with wrong info flag
  const wrongInfoRestaurants = (restaurants || []).filter(r => r.wrongInfoFlag && r.wrongInfoStatus === 'pending');

  // Get user who flagged it
  const getUserById = (userId) => {
    return (users || []).find(u => u.id === userId);
  };

  // Get wrong info type label
  const getWrongInfoTypeLabel = (type) => {
    const labels = {
      'wrong_number': 'رقم هاتف خاطئ',
      'wrong_name': 'اسم المطعم خاطئ',
      'wrong_location': 'موقع خاطئ',
      'duplicate': 'مكرر',
      'closed': 'المطعم مغلق',
      'other': 'أخرى',
    };
    return labels[type] || type;
  };

  // Handle admin action
  const handleReview = async (restaurant) => {
    if (!action) {
      showToast('⚠️ يرجى اختيار إجراء', 'warning');
      return;
    }

    setReviewingId(restaurant.id);

    try {
      const updates = {
        wrongInfoAdminNote: adminNote || null,
        wrongInfoReviewedBy: currentUser.id,
        wrongInfoReviewedAt: new Date().toISOString(),
      };

      if (action === 'correct') {
        // Return to user with corrections
        updates.wrongInfoStatus = 'corrected';
        showToast('✅ تم تصحيح المعلومات وإعادة التواصل');
      } else if (action === 'archive') {
        // Archive the contact
        updates.wrongInfoStatus = 'archived';
        updates.isArchived = true;
        showToast('✅ تم أرشفة المطعم');
      } else if (action === 'return') {
        // Return to user with note
        updates.wrongInfoStatus = 'returned';
        showToast('✅ تم إعادة التواصل للمستخدم');
      }

      await updateRestaurant(restaurant.id, updates);
      setReviewingId(null);
      setAdminNote('');
      setAction('');
      
      // Reload data
      if (ctx.loadRestaurants) {
        ctx.loadRestaurants();
      }
    } catch (err) {
      console.error('Review error:', err);
      showToast('❌ فشل في المراجعة', 'error');
      setReviewingId(null);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '—';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 1) return 'منذ أقل من ساعة';
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    return date.toLocaleDateString('ar-AE');
  };

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 32px)' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: '#000000', fontSize: 28, fontWeight: 900, margin: '0 0 8px' }}>
          <AlertCircle size={32} color="#f59e0b" style={{ marginLeft: 12, verticalAlign: 'middle' }} />
          مراجعة المعلومات الخاطئة
        </h1>
        <p style={{ color: '#000000', fontSize: 14, margin: 0 }}>
          جهات التواصل التي تم الإبلاغ عنها للمراجعة
        </p>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 32
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #f59e0b',
          borderRadius: 12,
          padding: 20,
        }}>
          <p style={{ color: '#fcd34d', fontSize: 12, marginBottom: 8 }}>بانتظار المراجعة</p>
          <p style={{ color: '#f59e0b', fontSize: 32, fontWeight: 900, margin: 0 }}>
            {wrongInfoRestaurants.length}
          </p>
        </div>
      </div>

      {/* List */}
      {wrongInfoRestaurants.length === 0 ? (
        <div style={{
          background: '#ffffff',
          borderRadius: 12,
          padding: 60,
          textAlign: 'center',
          color: '#64748b'
        }}>
          <CheckCircle size={64} color="#10b981" style={{ marginBottom: 16 }} />
          <p style={{ fontSize: 18, fontWeight: 700 }}>لا توجد بلاغات بانتظار المراجعة</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 20 }}>
          {wrongInfoRestaurants.map(restaurant => {
            const flaggedBy = getUserById(restaurant.wrongInfoFlaggedBy);
            const isReviewing = reviewingId === restaurant.id;

            return (
              <div
                key={restaurant.id}
                style={{
                  background: '#ffffff',
                  borderRadius: 12,
                  border: '1px solid #e2e8f0',
                  padding: 24,
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ color: '#000000', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>
                      🍽️ {restaurant.name}
                    </h3>
                    <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 4px' }}>
                      📍 {restaurant.city || 'غير محدد'} - {restaurant.district || 'غير محدد'}
                    </p>
                  </div>
                  <div style={{
                    background: '#f59e0b22',
                    color: '#f59e0b',
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 700,
                  }}>
                    {formatTimeAgo(restaurant.wrongInfoFlaggedAt)}
                  </div>
                </div>

                {/* Wrong Info Details */}
                <div style={{
                  background: '#ffffff',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 16
                }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div>
                      <span style={{ color: '#000000', fontSize: 12 }}>نوع الخطأ:</span>
                      <span style={{ color: '#000000', fontSize: 14, fontWeight: 700, marginRight: 8 }}>
                        {getWrongInfoTypeLabel(restaurant.wrongInfoType)}
                      </span>
                    </div>
                    {restaurant.wrongInfoDescription && (
                      <div>
                        <span style={{ color: '#000000', fontSize: 12 }}>الوصف:</span>
                        <p style={{ color: '#000000', fontSize: 13, margin: '4px 0 0' }}>
                          {restaurant.wrongInfoDescription}
                        </p>
                      </div>
                    )}
                    {flaggedBy && (
                      <div>
                        <span style={{ color: '#000000', fontSize: 12 }}>أبلغ بواسطة:</span>
                        <span style={{ color: '#4ecdc4', fontSize: 13, fontWeight: 700, marginRight: 8 }}>
                          {flaggedBy.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Review Section */}
                <div style={{
                  background: '#e2e8f0',
                  borderRadius: 8,
                  padding: 16,
                  border: '1px solid #4ecdc444'
                }}>
                  <h4 style={{ color: '#4ecdc4', fontSize: 14, fontWeight: 700, margin: '0 0 12px' }}>
                    <Edit3 size={16} style={{ marginLeft: 6, verticalAlign: 'middle' }} />
                    إجراء المراجعة
                  </h4>

                  {/* Action Selection */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                    <button
                      onClick={() => setAction('correct')}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: action === 'correct' ? '2px solid #10b981' : '1px solid #e2e8f0',
                        background: action === 'correct' ? '#10b98122' : '#ffffff',
                        color: action === 'correct' ? '#10b981' : '#94a3b8',
                        cursor: 'pointer',
                        fontFamily: "'Cairo',sans-serif",
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                    >
                      <CheckCircle size={14} />
                      تصحيح وإعادة
                    </button>

                    <button
                      onClick={() => setAction('return')}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: action === 'return' ? '2px solid #4ecdc4' : '1px solid #e2e8f0',
                        background: action === 'return' ? '#4ecdc422' : '#ffffff',
                        color: action === 'return' ? '#4ecdc4' : '#94a3b8',
                        cursor: 'pointer',
                        fontFamily: "'Cairo',sans-serif",
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                    >
                      <Send size={14} />
                      إعادة للمستخدم
                    </button>

                    <button
                      onClick={() => setAction('archive')}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: action === 'archive' ? '2px solid #ef4444' : '1px solid #e2e8f0',
                        background: action === 'archive' ? '#ef444422' : '#ffffff',
                        color: action === 'archive' ? '#ef4444' : '#94a3b8',
                        cursor: 'pointer',
                        fontFamily: "'Cairo',sans-serif",
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                    >
                      <Archive size={14} />
                      أرشفة
                    </button>
                  </div>

                  {/* Admin Note */}
                  <textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="اكتب ملاحظة أو التصحيحات..."
                    rows={3}
                    style={{
                      width: '100%',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '10px 12px',
                      color: '#000000',
                      fontSize: 13,
                      fontFamily: "'Cairo',sans-serif",
                      resize: 'vertical',
                      marginBottom: 12,
                      boxSizing: 'border-box',
                    }}
                  />

                  {/* Submit Button */}
                  <button
                    onClick={() => handleReview(restaurant)}
                    disabled={isReviewing || !action}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: (!action || isReviewing) ? '#e2e8f0' : '#10b981',
                      color: (!action || isReviewing) ? '#64748b' : '#fff',
                      cursor: (!action || isReviewing) ? 'not-allowed' : 'pointer',
                      fontFamily: "'Cairo',sans-serif",
                      fontSize: 14,
                      fontWeight: 700,
                      opacity: (!action || isReviewing) ? 0.6 : 1,
                    }}
                  >
                    {isReviewing ? 'جاري المعالجة...' : 'تأكيد المراجعة'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
